import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { Box, Button, Card, CardContent, Divider, Grid, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorPage403 from 'src/pages/error/403';
import { useEffect, useMemo, useState } from 'react';
import useConfigs, { Configs } from './useConfigs';
import { resetConfigs, updateConfigs, retrieveReferralPool, updateReferralPool } from 'src/pages/admin/services/whitelistService';

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [baseNumber, setBaseNumber] = useState<number | ''>('');
  const [intervalMs, setIntervalMs] = useState<number | ''>('');
  const [minGrowth, setMinGrowth] = useState<number | ''>('');
  const [maxGrowth, setMaxGrowth] = useState<number | ''>('');

  const [unlockVolume, setUnlockVolume] = useState('');
  const [unlockVolumeError, setUnlockVolumeError] = useState('');

  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [earlyAccessAt, setEarlyAccessAt] = useState('');
  const [eventScheduleErrors, setEventScheduleErrors] = useState<{
    endAt?: string;
    earlyAccessAt?: string;
  }>({});

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [referralPoolTotal, setReferralPoolTotal] = useState('');
  const [referralPoolData, setReferralPoolData] = useState<{
    totalUsdc: string;
    committedUsdc: string;
    remainingUsdc: string;
    totalDistributedUsdc: string;
    lastUpdatedAt: number;
  } | null>(null);
  const [referralPoolError, setReferralPoolError] = useState('');

  const { configsData: candidateData, retrieve } = useConfigs();

  const fetchReferralPool = async () => {
    try {
      const res = await retrieveReferralPool();
      const data = res.data?.data ?? res.data;
      setReferralPoolData(data);
      setReferralPoolTotal(data?.totalUsdc ?? '');
    } catch (error: any) {
      console.log('error fetching referral pool:', error);
    }
  };

  useEffect(() => {
    retrieve();
    fetchReferralPool();
  }, [retrieve]);

  useEffect(() => {
    if (candidateData) {
      setBaseNumber(candidateData.baseCount ?? '');
      setIntervalMs(candidateData.growthIntervalMs ?? '');
      setMinGrowth(candidateData.growthMin ?? '');
      setMaxGrowth(candidateData.growthMax ?? '');
      setUnlockVolume(
        candidateData.referralMinTradeUsdc != null ? String(candidateData.referralMinTradeUsdc) : ''
      );
      setStartAt(msToDatetimeLocal(candidateData.startAt));
      setEndAt(msToDatetimeLocal(candidateData.endAt));
      setEarlyAccessAt(msToDatetimeLocal(candidateData.earlyAccessAt));
    }
  }, [candidateData]);

  const msToDatetimeLocal = (ms: number | undefined): string => {
    if (!ms) return '';
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const datetimeLocalToMs = (s: string): number => {
    if (!s) return 0;
    return new Date(s).getTime();
  };

  const validatePositiveInteger = (value: number | '', fieldName: string, minValue = 0): string => {
    if (value === '') {
      return `${fieldName} cannot be empty`;
    }

    if (!Number.isInteger(value)) {
      return `${fieldName} must be an integer`;
    }

    if (value <= minValue) {
      return `${fieldName} must be greater than ${minValue}`;
    }

    return '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    newErrors.baseNumber = validatePositiveInteger(baseNumber, 'Initial Display Count');

    newErrors.intervalMs = validatePositiveInteger(intervalMs, 'Growth Interval', 100);

    newErrors.minGrowth = validatePositiveInteger(minGrowth, 'Minimum Single Growth');

    newErrors.maxGrowth = validatePositiveInteger(maxGrowth, 'Maximum Single Growth');

    if (minGrowth !== '' && maxGrowth !== '' && Number(minGrowth) > Number(maxGrowth)) {
      newErrors.minGrowth = 'Minimum Single Growth must not be greater than Maximum Single Growth';
    }

    setErrors(newErrors);

    return Object.values(newErrors).every((v) => !v);
  };

  const handleIntegerInput =
    (setter: (value: number | '') => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // allow empty
      if (value === '') {
        setter('');
        return;
      }

      // only positive integers
      if (/^\d+$/.test(value)) {
        setter(Number(value));
      }
    };

  const calculateGrowthPerMinute = (
    intervalMs: number,
    minGrowth: number,
    maxGrowth: number
  ): string => {
    const avgGrowth = (minGrowth + maxGrowth) / 2;
    const intervalsPerMinute = 60000 / intervalMs;

    return (avgGrowth * intervalsPerMinute).toFixed(2);
  };

  const growthPerMinute = useMemo(() => {
    if (!candidateData) return;
    return calculateGrowthPerMinute(
      Number(candidateData.growthIntervalMs),
      Number(candidateData.growthMin),
      Number(candidateData.growthMax)
    );
  }, [candidateData]);

  const growthRangeDisplay = `${candidateData?.growthMin}~${candidateData?.growthMax} ${translate(
    'whitelist.personUnit'
  )}`;

  const handleSave = async () => {
    const isValid = validateForm();

    if (!isValid) return;

    const candidate: Configs = {
      baseCount: Number(baseNumber),
      growthIntervalMs: Number(intervalMs),
      growthMin: Number(minGrowth),
      growthMax: Number(maxGrowth),
    };

    try {
      const updated = await updateConfigs(candidate);

      if (updated?.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieve();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleSaveEventSchedule = async () => {
    const startAtMs = datetimeLocalToMs(startAt);
    const endAtMs = datetimeLocalToMs(endAt);
    const earlyAccessAtMs = datetimeLocalToMs(earlyAccessAt);

    const newErrors: { endAt?: string; earlyAccessAt?: string } = {};

    if (!endAt) {
      newErrors.endAt = translate('whitelist.eventSchedule.endAtRequired');
    } else if (startAtMs === 0 && endAtMs <= Date.now()) {
      newErrors.endAt = translate('whitelist.eventSchedule.endAtMustBeFuture');
    } else if (startAtMs > 0 && endAtMs <= startAtMs) {
      newErrors.endAt = translate('whitelist.eventSchedule.endAtAfterStartAt');
    }

    if (!earlyAccessAt) {
      newErrors.earlyAccessAt = translate('whitelist.eventSchedule.earlyAccessAtRequired');
    } else if (startAtMs > 0 && earlyAccessAtMs >= startAtMs) {
      newErrors.earlyAccessAt = translate('whitelist.eventSchedule.earlyAccessBeforeStartAt');
    }
    setEventScheduleErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const res = await updateConfigs({startAt: startAtMs, endAt: endAtMs, earlyAccessAt: earlyAccessAtMs });
      if (res?.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieve();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const validateReferralPoolTotal = (value: string): string => {
    if (!value.trim()) return translate('whitelist.referralPool.totalRequired');
    if (Number.isNaN(Number(value)) || Number(value) < 0)
      return translate('whitelist.referralPool.totalInvalid');
    if (
      referralPoolData?.committedUsdc != null &&
      Number(value) < Number(referralPoolData.committedUsdc)
    ) {
      return translate('whitelist.referralPool.totalBelowCommitted', {
        committed: referralPoolData.committedUsdc,
      });
    }
    return '';
  };

  const referralPoolSaveDisabled = !!validateReferralPoolTotal(referralPoolTotal);

  const handleSaveReferralPool = async () => {
    const err = validateReferralPoolTotal(referralPoolTotal);
    if (err) {
      setReferralPoolError(err);
      return;
    }
    setReferralPoolError('');
    try {
      const res = await updateReferralPool(referralPoolTotal);
      if (res.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        const data = res.data?.data ?? res.data;
        setReferralPoolData(data);
        setReferralPoolTotal(data?.totalUsdc ?? referralPoolTotal);
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleSaveUnlockVolume = async () => {
    if (!unlockVolume.trim()) {
      setUnlockVolumeError(translate('whitelist.unlockVolume.required'));
      return;
    }
    if (Number.isNaN(Number(unlockVolume)) || Number(unlockVolume) < 0) {
      setUnlockVolumeError(translate('whitelist.unlockVolume.invalid'));
      return;
    }
    setUnlockVolumeError('');
    try {
      const res = await updateConfigs({ referralMinTradeUsdc: unlockVolume });
      if (res?.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieve();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleReset = async () => {
    try {
      const resposne = await resetConfigs();

      if (resposne.data) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        retrieve();
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error');
      notify(error?.response?.data?.message || translate('whitelist.failed'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-candidate" action="read" fallback={<ErrorPage403 />}>
        <Card sx={{ mt: 3, pb: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom fontWeight="medium" sx={{ mb: 3 }}>
              {translate('whitelist.candidate.title')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.candidate.initShowCount')}
                  value={baseNumber}
                  onChange={handleIntegerInput(setBaseNumber)}
                  error={!!errors.baseNumber}
                  helperText={errors.baseNumber}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate('whitelist.personUnit')}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.candidate.growthInterval')}
                  value={intervalMs}
                  onChange={handleIntegerInput(setIntervalMs)}
                  error={!!errors.intervalMs}
                  helperText={
                    errors.intervalMs || translate('whitelist.candidate.growthDescription')
                  }
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.candidate.minSingleGrowth')}
                  value={minGrowth}
                  onChange={handleIntegerInput(setMinGrowth)}
                  error={!!errors.minGrowth}
                  helperText={errors.minGrowth}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate('whitelist.personUnit')}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.candidate.maxSingleGrowth')}
                  value={maxGrowth}
                  onChange={handleIntegerInput(setMaxGrowth)}
                  error={!!errors.maxGrowth}
                  helperText={errors.maxGrowth}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {translate('whitelist.personUnit')}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <PermissionGuard resource="whitelist-candidate" action="update" fallback={null}>
              <Box sx={{ display: 'flex', gap: 2, mt: 4, mb: 3 }}>
                <Button variant="contained" onClick={handleSave}>
                  {translate('whitelist.save')}
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  {translate('whitelist.reset')}
                </Button>
              </Box>
            </PermissionGuard>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom fontWeight="medium">
              {translate('whitelist.candidate.currentEffectiveConfiguration')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.candidate.baseNumberPeople')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {candidateData?.baseCount} {translate('whitelist.personUnit')}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.candidate.growthInterval')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {candidateData?.growthIntervalMs} ms
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.candidate.singleGrowth')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {growthRangeDisplay}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.candidate.estimatedGrowth')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="primary.main">
                    {growthPerMinute} {translate('whitelist.personUnit')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </CardContent>
        </Card>
      <Card sx={{ mt: 3, pb: 2 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="medium" sx={{ mb: 3 }}>
            {translate('whitelist.eventSchedule.title')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={translate('whitelist.eventSchedule.startAt')}
                type="datetime-local"
                value={startAt}
                onChange={(e) => {
                  setStartAt(e.target.value);
                  setEventScheduleErrors({});
                }}
                helperText={translate('whitelist.eventSchedule.startAtHelper')}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: startAt ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setStartAt('');
                          setEventScheduleErrors({});
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={translate('whitelist.eventSchedule.endAt')}
                type="datetime-local"
                value={endAt}
                onChange={(e) => {
                  setEndAt(e.target.value);
                  setEventScheduleErrors((prev) => ({ ...prev, endAt: undefined }));
                }}
                error={!!eventScheduleErrors.endAt}
                helperText={eventScheduleErrors.endAt || ' '}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={translate('whitelist.eventSchedule.earlyAccessAt')}
                type="datetime-local"
                value={earlyAccessAt}
                onChange={(e) => {
                  setEarlyAccessAt(e.target.value);
                  setEventScheduleErrors((prev) => ({ ...prev, earlyAccessAt: undefined }));
                }}
                error={!!eventScheduleErrors.earlyAccessAt}
                helperText={eventScheduleErrors.earlyAccessAt || ' '}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <PermissionGuard resource="whitelist-candidate" action="update" fallback={null}>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleSaveEventSchedule}>
                {translate('whitelist.save')}
              </Button>
            </Box>
          </PermissionGuard>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom fontWeight="medium">
            {translate('whitelist.candidate.currentEffectiveConfiguration')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.eventSchedule.startAt')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {candidateData?.startAt
                    ? new Date(candidateData.startAt).toLocaleString()
                    : translate('whitelist.eventSchedule.notConfigured')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.eventSchedule.endAt')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {candidateData?.endAt
                    ? new Date(candidateData.endAt).toLocaleString()
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {translate('whitelist.eventSchedule.earlyAccessAt')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {candidateData?.earlyAccessAt
                    ? new Date(candidateData.earlyAccessAt).toLocaleString()
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </CardContent>
      </Card>
      </PermissionGuard>

      <PermissionGuard resource="whitelist-candidate" action="read" fallback={<ErrorPage403 />}>
        <Card sx={{ mt: 3, pb: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom fontWeight="medium" sx={{ mb: 3 }}>
              {translate('whitelist.referralPool.title')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.referralPool.totalUsdc')}
                  value={referralPoolTotal}
                  onChange={(e) => {
                    const val = e.target.value;
                    setReferralPoolTotal(val);
                    setReferralPoolError(validateReferralPoolTotal(val));
                  }}
                  error={!!referralPoolError}
                  helperText={referralPoolError}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <PermissionGuard resource="whitelist-candidate" action="update" fallback={null}>
              <Box sx={{ mt: 3, mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveReferralPool}
                  disabled={referralPoolSaveDisabled}
                >
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom fontWeight="medium">
              {translate('whitelist.candidate.currentEffectiveConfiguration')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.referralPool.totalUsdc')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {referralPoolData?.totalUsdc ?? '-'} USDT
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.referralPool.committedUsdc')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="warning.main">
                    {referralPoolData?.committedUsdc ?? '-'} USDT
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.referralPool.remainingUsdc')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="success.main">
                    {referralPoolData?.remainingUsdc ?? '-'} USDT
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('whitelist.referralPool.unrewardedCount')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {referralPoolData?.totalDistributedUsdc != null
                      ? `${referralPoolData.totalDistributedUsdc} USDT`
                      : '-'}
                  </Typography>
                </Grid>
                {referralPoolData?.lastUpdatedAt && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('whitelist.referralPool.lastUpdatedAt')}:{' '}
                      {new Date(referralPoolData.lastUpdatedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3, pb: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom fontWeight="medium" sx={{ mb: 3 }}>
              {translate('whitelist.unlockVolume.title')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('whitelist.unlockVolume.fieldLabel')}
                  value={unlockVolume}
                  onChange={(e) => {
                    setUnlockVolume(e.target.value);
                    setUnlockVolumeError('');
                  }}
                  error={!!unlockVolumeError}
                  helperText={unlockVolumeError}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDT</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <PermissionGuard resource="whitelist-candidate" action="update" fallback={null}>
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={handleSaveUnlockVolume}>
                  {translate('whitelist.save')}
                </Button>
              </Box>
            </PermissionGuard>
          </CardContent>
        </Card>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
